/**
 * Header Component
 *
 * Desktop (>md): logo left, nav links + icons right.
 * Mobile/Tablet (<=md): hamburger left, logo center, cart right.
 *   Nav links move into a Drawer that slides from the left with
 *   focus-trap, search shortcut, and full ARIA support.
 */

'use client';

import { Icon, Drawer } from '@template-project/afrodita-components';
import { useNavigate, useLocation } from 'react-router-dom';
import { useCartContext } from '@/lib/context/cart-context';
import { useCart } from '@/lib/hooks/use-cart';
import { authService } from '@/lib/services/auth.service';
import { Tooltip } from '@/components/Tooltip/Tooltip';
import { CountrySelector } from '@/components/CountrySelector/CountrySelector';
import { FocusTrapWrapper } from '@/components/FocusTrapWrapper';
import { SearchModal } from '@/components/organisms/SearchModal';
import { useIntl } from '@/lib/intl';
import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavCollections, useSaleCollection } from '@/lib/hooks/use-nav-collections';
import {
  HeaderContainer,
  Logo,
  LogoText,
  DesktopNav,
  NavLink,
  SaleLink,
  MobileLeft,
  MobileRight,
  IconButton,
  CartBadge,
  DrawerCloseRow,
  SearchShortcut,
  DrawerNav,
  DrawerNavLink,
  DrawerSaleLink,
  DrawerSection,
} from './Header.styled';


const SALE_COLLECTION_SLUG = 'summer-sale';
const MAX_NAV_LINKS = 7;
const MOBILE_MENU_ID = 'mobile-nav-drawer';

/* ------------------------------------------------------------------ */
/*  Inline SVGs                                                        */
/* ------------------------------------------------------------------ */

const UserIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
  </svg>
);

const HamburgerIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <line x1="3" y1="6" x2="21" y2="6" />
    <line x1="3" y1="12" x2="21" y2="12" />
    <line x1="3" y1="18" x2="21" y2="18" />
  </svg>
);

const CloseIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <line x1="6" y1="6" x2="18" y2="18" />
    <line x1="6" y1="18" x2="18" y2="6" />
  </svg>
);

const SearchIcon = () => (
  <svg width="24" height="24" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
  </svg>
);

/* ------------------------------------------------------------------ */
/*  Header Component                                                   */
/* ------------------------------------------------------------------ */

interface HeaderProps {
  siteTitle?: string;
}

export function Header({ siteTitle = 'Shapermint' }: Readonly<HeaderProps>) {
  const navigate = useNavigate();
  const location = useLocation();
  const { localizedPath } = useIntl();
  const { showCart, cartToken, clearCart } = useCartContext();
  const { data: cart } = useCart(cartToken, clearCart);
  const { collections: navCollections } = useNavCollections();
  const saleData = useSaleCollection();
  const saleLabel = saleData?.title ?? 'Summer Sale';

  const [displayName, setDisplayName] = useState<string | null>(null);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);

  const hamburgerRef = useRef<HTMLButtonElement>(null);
  const searchButtonRef = useRef<HTMLButtonElement>(null);

  const openSearch = useCallback(() => setIsSearchOpen(true), []);
  const closeSearch = useCallback(() => setIsSearchOpen(false), []);

  const itemCount = cart?.items.reduce((sum, item) => sum + item.quantity, 0) || 0;

  const closeMenu = useCallback(() => setMenuOpen(false), []);

  useEffect(() => {
    const checkSession = async () => {
      const storedDisplayName = authService.getDisplayName();
      setDisplayName(storedDisplayName);

      if (authService.isAuthenticated()) {
        setIsLoggedIn(true);
      } else if (authService.hasSession()) {
        const result = await authService.tryRestoreSession();
        setIsLoggedIn(result.success);
        if (!result.success) setDisplayName(null);
      } else {
        setIsLoggedIn(false);
      }
    };
    checkSession();
  }, [location.pathname]);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  const handleUserIconClick = () => {
    if (isLoggedIn) {
      navigate(localizedPath('/my-account'));
    } else {
      navigate(localizedPath('/login'));
    }
  };

  const handleSearchClick = () => {
    closeMenu();
    openSearch();
  };

  const userIcon = (
    <IconButton onClick={handleUserIconClick} aria-label="My account">
      <UserIcon />
    </IconButton>
  );

  const cartButton = (
    <IconButton onClick={showCart} aria-label="Open cart">
      <Icon.PDP.ShoppingBag width={24} height={24} />
      {itemCount > 0 && <CartBadge>{itemCount}</CartBadge>}
    </IconButton>
  );

  return (
    <>
      <HeaderContainer>
        <MobileLeft>
          <IconButton
            ref={hamburgerRef}
            onClick={() => setMenuOpen((v) => !v)}
            aria-label={menuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={menuOpen}
            aria-controls={MOBILE_MENU_ID}
          >
            {menuOpen ? <CloseIcon /> : <HamburgerIcon />}
          </IconButton>
        </MobileLeft>

        <Logo to={localizedPath('/')}>
          <LogoText variant="body" weight="bold">
            {siteTitle}
          </LogoText>
        </Logo>

        <DesktopNav>
          {navCollections.slice(0, MAX_NAV_LINKS).map(({ label, slug }) => (
            <NavLink key={slug} to={localizedPath(`/collections/${slug}`)}>
              {label}
            </NavLink>
          ))}

          <SaleLink to={localizedPath(`/collections/${SALE_COLLECTION_SLUG}`)}>
            {saleLabel}
          </SaleLink>

          <IconButton
            ref={searchButtonRef}
            onClick={openSearch}
            aria-label="Search products"
          >
            <SearchIcon />
          </IconButton>
          <CountrySelector />
          {isLoggedIn && displayName ? (
            <Tooltip content={displayName} position="bottom">
              {userIcon}
            </Tooltip>
          ) : (
            userIcon
          )}
          {cartButton}
        </DesktopNav>

        <MobileRight>
          <IconButton onClick={openSearch} aria-label="Search products">
            <SearchIcon />
          </IconButton>
          {cartButton}
        </MobileRight>
      </HeaderContainer>

      <Drawer
        isOpen={menuOpen}
        onClose={closeMenu}
        position="left"
        width="80%"
      >
        <FocusTrapWrapper
          isOpen={menuOpen}
          onClose={closeMenu}
          triggerRef={hamburgerRef}
        >
          <dialog id={MOBILE_MENU_ID} open aria-label="Navigation menu" style={{ display: 'contents', padding: 0, border: 'none', background: 'none' }}>
            <DrawerCloseRow>
              <IconButton onClick={closeMenu} aria-label="Close menu">
                <CloseIcon />
              </IconButton>
            </DrawerCloseRow>

            <SearchShortcut onClick={handleSearchClick} aria-label="Search products">
              <SearchIcon />
              Search products...
            </SearchShortcut>

            <DrawerNav>
              {navCollections.slice(0, MAX_NAV_LINKS).map(({ label, slug }) => (
                <DrawerNavLink key={slug} to={localizedPath(`/collections/${slug}`)} onClick={closeMenu}>
                  {label}
                </DrawerNavLink>
              ))}
              <DrawerSaleLink to={localizedPath(`/collections/${SALE_COLLECTION_SLUG}`)} onClick={closeMenu}>
                {saleLabel}
              </DrawerSaleLink>
            </DrawerNav>

            <DrawerSection>
              <CountrySelector />

              <IconButton onClick={() => { handleUserIconClick(); closeMenu(); }} aria-label="My account" style={{ justifyContent: 'flex-start', gap: '8px' }}>
                <UserIcon />
                <span style={{ fontSize: '16px' }}>{isLoggedIn && displayName ? displayName : 'My Account'}</span>
              </IconButton>
            </DrawerSection>
          </dialog>
        </FocusTrapWrapper>
      </Drawer>

      <SearchModal
        isOpen={isSearchOpen}
        onClose={closeSearch}
        triggerRef={searchButtonRef}
      />
    </>
  );
}
